import type { ReactNode } from 'react';
import { tokens, Stack, Text } from '@hotjar/hotsauce';
import styled from '@emotion/styled';

const QuestionIndex = styled.span`
  ${tokens.text('subtitle2')}
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${tokens.spacer.x4};
  height: ${tokens.spacer.x4};
  background: ${tokens.color.neutral_200};
  border-radius: 50px;
  flex-shrink: 0;
`;

const DragHandleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-items: center;
  flex-shrink: 0;
  flex-grow: 0;
  justify-content: center;
  width: 100%;
  color: ${tokens.color.neutral_300};
`;

export const QuestionFooter = styled(Stack)`
  background: ${tokens.color.neutral_100};
  margin: -${tokens.spacer.x2};
  margin-top: 0;
  padding: ${tokens.spacer.x2};
  flex-grow: 1;
  ${tokens.transition('background')};
`;

const QuestionBorder = styled(Stack)`
  background: ${tokens.color.white};
  border: 1px solid ${tokens.color.neutral_200};
  border-radius: ${tokens.radius.m};
  align-items: stretch;
  padding: ${tokens.spacer.x2};

  flex-grow: 1;

  > label {
    /* reset for insights.scss */
    margin-bottom: 0;
  }

  ${(props: { isDraggable: boolean; isDragging: boolean }) =>
    props.isDraggable &&
    `
    ${tokens.transition('background')};

    :hover {
      ${tokens.transition('box-shadow')};
      ${tokens.elevation(4)};
      ${DragHandleContainer} {
        ${tokens.transition('color')};
        color: ${tokens.color.neutral_400};
      }
    }

    ${
      props.isDragging &&
      `
      ${tokens.transition('box-shadow')};
      ${tokens.elevation(4)};
      `
    }
    `}
`;

const DragHandle = (): JSX.Element => {
  return (
    <DragHandleContainer>
      <svg xmlns="http://www.w3.org/2000/svg" width="34" height="10">
        <g fill="currentColor">
          <circle cx="2" cy="2" r="2" />
          <circle cx="8" cy="2" r="2" />
          <circle cx="14" cy="2" r="2" />
          <circle cx="20" cy="2" r="2" />
          <circle cx="26" cy="2" r="2" />
          <circle cx="32" cy="2" r="2" />
          <circle cx="2" cy="8" r="2" />
          <circle cx="8" cy="8" r="2" />
          <circle cx="14" cy="8" r="2" />
          <circle cx="20" cy="8" r="2" />
          <circle cx="26" cy="8" r="2" />
          <circle cx="32" cy="8" r="2" />
        </g>
      </svg>
    </DragHandleContainer>
  );
};

export const QuestionContainer = ({
  index,
  title,
  children,
  isDraggable = true,
  isDragging = false,
  onMouseEnter,
}: {
  index: number;
  title?: string;
  children: ReactNode;
  isDraggable?: boolean;
  isDragging?: boolean;
  onMouseEnter: () => void;
}): JSX.Element => {
  return (
    <Stack direction="row" spacing="x1" onMouseEnter={onMouseEnter}>
      <QuestionIndex title={`Step ${index + 1}`}>{index + 1}</QuestionIndex>
      <QuestionBorder direction="column" isDraggable={isDraggable} isDragging={isDragging}>
        {isDraggable && <DragHandle />}
        {title && <Text textType="subtitle2">{title}</Text>}
        {children}
      </QuestionBorder>
    </Stack>
  );
};
